import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../components/loader/Loader";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import {
  assignTicket,
  closeTicket,
  rejectTicket,
} from "../../redux/actions/TicketActions";
import { getTicketDocuments } from "../../redux/actions/DocumentActions";
import { updateOdooAccount } from "../../redux/actions/CompanyActions";
import { FiArrowLeft } from "react-icons/fi";
import { StatusBadge } from "../../components/dashboard/DashboardUI";
import { DocumentList } from "../../components/modals/RequestDetailModal";
import { ACTIVITY_SECTORS } from "../../utils/requestDetails";

const TICKET_STATUS = {
  initiated: "En attente",
  in_progress: "En cours",
  fulfilled: "Terminée",
  failed: "Échouée",
};

countries.registerLocale(fr);

function OperatorTicketDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { ticket } = location.state || {};
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const { token } = useSelector((state) => state.authUser.user);
  const [formData, setFormData] = useState({ comment: "" });
  const [formDataOdoo, setFormDataOdoo] = useState({
    url: "",
    email: "",
    password: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [showOdooCreationModal, setShowOdooCreationModal] = useState(false);
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState(null);
  const handleModalClose = () => setShowModal(false);
  const handleModalShow = (action) => {
    setAction(action);
    setShowModal(true);
  };

  const handleOdooCreationModalClose = () => setShowOdooCreationModal(false);
  const handleOdooCreationModalShow = (action) => {
    setAction(action);
    setShowOdooCreationModal(true);
  };

  useEffect(() => {
    const fetchAttachments = async () => {
      if (ticket && user?.token && ticket?.reference) {
        setLoading(true);
        try {
          await dispatch(
            getTicketDocuments(user.token, ticket.reference, (data) => {
              if (data) {
                setAttachments(data);
              }
            }),
          );
        } catch (error) {
          console.error("Erreur lors du chargement des documents :", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAttachments();
  }, [ticket, user?.token, ticket?.reference, dispatch]);

  const handleAction = (action) => {
    Swal.fire({
      title: `Voulez-vous vraiment ${
        action === "in_progress"
          ? "traiter"
          : action === "fulfilled"
            ? "clôturer"
            : action === "failed" && "rejeter"
      } ce ticket ?`,
      text: "Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#ccc",
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setSubmitting(true);

        if (action === "in_progress") {
          dispatch(
            assignTicket(
              token,
              { reference: ticket?.reference },
              handleCallback,
            ),
          );
        } else if (action === "fulfilled") {
          if (ticket.reclamationCode === "odoo" && ticket.createOdoo) {
            await dispatch(
              updateOdooAccount(
                token,
                ticket?.company._id,
                { ...formDataOdoo },
                handleOdooCallback,
              ),
            );
          } else {
            await dispatch(
              closeTicket(
                token,
                {
                  reference: ticket?.reference,
                  status: action,
                  comment: formData.comment,
                },
                handleCallback,
              ),
            );
          }
        } else if (action === "failed") {
          dispatch(
            rejectTicket(
              token,
              { reference: ticket?.reference, status: action },
              handleCallback,
            ),
          );
        }
      }
    });
  };

  const handleCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      Toast.fire({
        icon: "success",
        title: "Succès",
        text: message,
      });
      navigate(-1);
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const handleOdooCallback = ({ success, message }) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      dispatch(
        closeTicket(
          token,
          {
            reference: ticket?.reference,
            status: action,
            comment: "OK",
          },
          handleCallback,
        ),
      );
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      setSubmitting(false);
    }
  };

  const company = ticket?.company || {};
  const files = (attachments || []).map((file) => ({
    key: file._id || file.id || file.url,
    label: file.name,
    file,
  }));
  const canTake = ticket?.status === "initiated" && !ticket?.operator;
  const canClose = ticket?.status === "in_progress" && ticket?.operator;

  return (
    <>
      <Loader isLoading={submitting} />
      {loading ? (
        <p className="dash-loading">Chargement de la demande…</p>
      ) : (
        <>
          <div className="dash-page-header">
            <div>
              <button
                type="button"
                className="dash-back"
                onClick={() => navigate(-1)}
              >
                <FiArrowLeft aria-hidden="true" /> Retour
              </button>
              <h1 className="dash-page-title">{ticket?.type || "Demande"}</h1>
              <p className="dash-page-lead">
                #{ticket?.reference} ·{" "}
                {moment(ticket?.createdAt).format("DD/MM/YYYY")}
              </p>
            </div>
            <div className="dash-header-actions">
              <StatusBadge
                status={ticket?.status}
                label={TICKET_STATUS[ticket?.status]}
              />
              {canTake && (
                <button
                  type="button"
                  className="dash-button"
                  onClick={() => handleAction("in_progress")}
                >
                  Prendre en charge
                </button>
              )}
              {canClose && (
                <button
                  type="button"
                  className="dash-button"
                  onClick={() => {
                    if (
                      ticket.reclamationCode === "odoo" &&
                      ticket.createOdoo
                    ) {
                      handleOdooCreationModalShow("fulfilled");
                    } else {
                      handleModalShow("fulfilled");
                    }
                  }}
                >
                  Clôturer
                </button>
              )}
            </div>
          </div>

          <div className="dash-detail-grid">
            <section className="dash-card">
              <h2 className="dash-card-title">Description du besoin</h2>
              <p className="dash-pre">{ticket?.description || "—"}</p>
              {ticket?.operator && <DocumentList documents={files} />}
              {ticket?.comment && (
                <div className="dash-note">
                  <h3 className="dash-subtitle">Commentaire de clôture</h3>
                  <p className="dash-pre">{ticket.comment}</p>
                </div>
              )}
            </section>

            <aside className="dash-card">
              <h2 className="dash-card-title">Entreprise</h2>
              <dl className="dash-details is-stacked">
                <dt>Nom</dt>
                <dd>{company.businessName || "—"}</dd>
                <dt>Forme juridique</dt>
                <dd>{company.businessType || "—"}</dd>
                <dt>Secteur d'activité</dt>
                <dd>
                  {ACTIVITY_SECTORS[company.activitySector] ||
                    company.activitySector ||
                    "—"}
                </dd>
                <dt>Siège social</dt>
                <dd>{company.address || "—"}</dd>
                <dt>Pays</dt>
                <dd>
                  {countries.getName(company.country, "fr") ||
                    company.country ||
                    "—"}
                </dd>
                {company.bank && (
                  <>
                    <dt>Banque</dt>
                    <dd>{company.bank}</dd>
                  </>
                )}
              </dl>
            </aside>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Commentaire du ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAction(action);
            }}
          >
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                name="comment"
                rows={3}
                value={formData.comment}
                onChange={(e) => setFormData({ comment: e.target.value })}
                placeholder="Ecrivez un commentaire..."
                required
              />
            </Form.Group>
            <div className="text-end">
              <Button
                variant="secondary"
                onClick={handleModalClose}
                className="me-2"
              >
                Annuler
              </Button>
              <Button variant="primary" type="submit">
                Soumettre
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showOdooCreationModal}
        onHide={handleOdooCreationModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Paramètres de connexion Odoo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleAction(action);
            }}
          >
            <Form.Group className="mb-3">
              <Form.Label>Adresse de l'espace Odoo du client</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://entreprise.odoo.com"
                value={formDataOdoo.url}
                onChange={(e) =>
                  setFormDataOdoo({ ...formDataOdoo, url: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Entrez l'email de l'entreprise"
                value={formDataOdoo.email}
                onChange={(e) =>
                  setFormDataOdoo({ ...formDataOdoo, email: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mot de passe</Form.Label>
              <Form.Control
                type="password"
                placeholder="Entrez le mot de passe Odoo"
                value={formDataOdoo.password}
                onChange={(e) =>
                  setFormDataOdoo({ ...formDataOdoo, password: e.target.value })
                }
              />
            </Form.Group>

            <div className="text-end">
              <Button
                variant="secondary"
                onClick={handleOdooCreationModalClose}
                className="me-2"
              >
                Annuler
              </Button>
              <Button variant="primary" type="submit">
                Soumettre
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default OperatorTicketDetails;
